import React, { useEffect, useRef, useId } from 'react';
import './ConfirmDialog.css';

// Accessible confirmation dialog for destructive / high-privilege actions
// (remove passkey, delete user, grant admin). The site's one modal pattern:
// role="dialog" + aria-modal + aria-labelledby/describedby; Escape cancels;
// focus moves in (to Cancel — the safe default), is trapped, and is restored to
// the trigger on close. (WCAG 4.1.2, 2.1.2, 2.4.3)
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}) {
  const panelRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const returnFocusRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement;
    cancelBtnRef.current?.focus();

    const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCancel(); return; }
      if (e.key === 'Tab') {
        const items = panelRef.current ? Array.from(panelRef.current.querySelectorAll(FOCUSABLE)) : [];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      const trigger = returnFocusRef.current;
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div
        className="confirm-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="confirm-title" id={titleId}>{title}</h2>
        <p className="confirm-message" id={descId}>{message}</p>
        <div className="confirm-actions">
          <button type="button" className="confirm-cancel" ref={cancelBtnRef} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? 'confirm-ok confirm-destructive' : 'confirm-ok'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
